if (!customElements.get('dongxi-mobile-purchase')) {
  customElements.define('dongxi-mobile-purchase', class extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.productInfo = this.closest('product-info');
      if (!this.productInfo || this.closest('quick-add-modal, [role="dialog"]')) return;

      this.button = this.querySelector('[data-dx-purchase-button]');
      this.price = this.querySelector('[data-dx-purchase-price]');
      this.variant = this.querySelector('[data-dx-purchase-variant]');
      this.error = this.querySelector('[data-dx-purchase-error]');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      this.submit = () => {
        const original = this.originalButton;
        if (!original || original.disabled || original.getAttribute('aria-disabled') === 'true') return;
        // The original button submits its existing variant, quantity and line properties.
        original.click();
        this.schedule();
      };
      this.button.addEventListener('click', this.submit);
      window.addEventListener('scroll', this.schedule, { passive: true });
      window.addEventListener('resize', this.schedule, { passive: true });
      window.addEventListener('pageshow', this.schedule);
      this.productInfo.addEventListener('change', this.schedule);
      this.productObserver = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.schedule();
      });
      this.productObserver.observe(this.productInfo, {
        subtree: true, childList: true, characterData: true,
        attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden', 'value'],
      });
      this.overlayObserver = new MutationObserver((records) => {
        if (records.some(({ target }) => target === document.body ||
          target.matches('quick-add-modal, modal-dialog, product-modal, cart-drawer, .cart-notification, dialog'))) {
          this.schedule();
        }
      });
      this.overlayObserver.observe(document.body, {
        subtree: true, attributes: true, attributeFilter: ['class', 'open'],
      });
      this.schedule();
    }

    get originalButton() {
      return document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
    }

    update() {
      const original = this.originalButton;
      const priceRoot = this.productInfo.querySelector(`#price-${this.dataset.section}`);
      const nativeForm = original?.closest('form');
      const nativeVariant = nativeForm?.querySelector('[name="id"]');
      const blocked = !original || original.disabled || original.getAttribute('aria-disabled') === 'true' ||
        nativeVariant?.disabled || !nativeVariant?.value || this.productInfo.variantRequestPending;
      const originalLabel = original?.querySelector(':scope > span')?.textContent.trim();
      this.button.disabled = blocked;
      if (originalLabel && this.button.textContent !== originalLabel) this.button.textContent = originalLabel;
      this.button.setAttribute('aria-busy', String(original?.classList.contains('loading') || false));

      const priceNode = priceRoot?.querySelector('.price--on-sale .price__sale .price-item--sale') ||
        priceRoot?.querySelector('.price__regular .price-item--regular');
      const priceText = priceRoot?.classList.contains('hidden') ? '' : priceNode?.textContent.trim() || '';
      if (this.price.textContent !== priceText) this.price.textContent = priceText;
      const selector = this.productInfo.querySelector('variant-selects');
      const selectedValues = selector ? [...selector.querySelectorAll('input[type="radio"]:checked, select')]
        .map((input) => input.tagName === 'SELECT' ? input.selectedOptions[0]?.textContent.trim() : input.value)
        .filter(Boolean).join(' / ') : this.dataset.productTitle;
      if (this.variant.textContent !== selectedValues) this.variant.textContent = selectedValues;
      this.variant.title = selectedValues || '';

      this.updateError(original?.closest('product-form'));

      const overlay = document.querySelector('quick-add-modal[open], modal-dialog[open], product-modal[open], dialog[open], cart-drawer.active, .cart-notification.active');
      const locked = [...document.body.classList].some((name) => name.startsWith('overflow-hidden'));
      const footer = document.querySelector('footer.footer, footer');
      const footerVisible = footer && footer.getBoundingClientRect().top < window.innerHeight;
      // Show the bar whenever the real button is off screen, above or below the viewport.
      const originalRect = original ? original.getBoundingClientRect() : null;
      const originalOffscreen = Boolean(originalRect) &&
        (originalRect.bottom <= 0 || originalRect.top >= window.innerHeight);
      this.hidden = !this.mobile.matches || !originalOffscreen || Boolean(overlay) || locked || Boolean(footerVisible);
    }

    updateError(productForm) {
      const networkError = productForm?.querySelector('.product-form__network-error');
      const originalError = networkError?.querySelector('p') ||
        productForm?.querySelector('.product-form__error-message-wrapper:not([hidden]) .product-form__error-message');
      const errorText = originalError?.textContent.trim() || '';
      const action = networkError?.querySelector('a, button');
      if (this.errorText !== errorText || this.errorSourceAction !== action) {
        this.error.textContent = errorText;
        this.errorText = errorText;
        this.errorSourceAction = action;
        if (action && errorText) {
          const control = document.createElement('button');
          control.type = 'button';
          control.className = 'link underlined-link';
          control.textContent = action.textContent;
          control.addEventListener('click', () => {
            // Bring the original action into view. This does not resubmit the
            // cart POST or silently retry a selection from the sticky bar.
            if (!action.isConnected) return;
            action.scrollIntoView({ block: 'center', behavior: 'instant' });
            action.focus({ preventScroll: true });
            this.schedule();
          });
          this.error.append(' ', control);
        }
      }
      this.error.setAttribute('role', 'status');
      this.error.hidden = !errorText;
    }

    disconnectedCallback() {
      this.productObserver?.disconnect();
      this.overlayObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      if (!this.schedule) return;
      window.removeEventListener('scroll', this.schedule);
      window.removeEventListener('resize', this.schedule);
      window.removeEventListener('pageshow', this.schedule);
      this.productInfo?.removeEventListener('change', this.schedule);
      this.button?.removeEventListener('click', this.submit);
    }
  });
}


// The theme's complementary-products block lazy-loads through an
// IntersectionObserver that never fires for its empty placeholder, so the
// recommendations stay unloaded. Call the theme's own loader once the page is
// idle, only while the block is still empty; rendering is unchanged.
(() => {
  const fill = () => {
    document.querySelectorAll('product-recommendations.complementary-products').forEach((el) => {
      if (el.dataset.dxComplementaryRequested === 'true') return;
      if (!el.dataset.productId || typeof el.loadRecommendations !== 'function') return;
      if (el.querySelector('.card-wrapper')) return;
      el.dataset.dxComplementaryRequested = 'true';
      el.loadRecommendations(el.dataset.productId);
    });
  };
  if (document.readyState === 'complete') fill();
  else window.addEventListener('load', fill, { once: true });
})();
